import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import remarkGfm from "remark-gfm";
import {
  checkAiHealth,
  checkAiQuota,
  decideAiKnowledge,
  sendChatFeedback,
  sendChatMessage,
} from "../../services/aiApi";
import { SystemIcon } from "../ui";
import "./ChatBot.css";

const ReactMarkdown = lazy(() => import("react-markdown"));

const WELCOME_MESSAGE = [
  "안녕하세요. 해밀 MES AI 어시스턴트 해미리입니다.",
  "계란 입고, 재고, 출고, LOT 추적이나 화면 사용 방법을 물어보세요.",
].join("\n");

const QUICK_PROMPTS = [
  "오늘 들어온 계란 얼마나 돼?",
  "지금 계란 재고 얼마나 남았어?",
  "오늘 출고할 주문 보여줘",
  "LOT 번호로 계란 찾는 방법 알려줘",
];

const RESPONSE_STAGE_STEPS = [
  { delay: 0, label: "질문 이해 중" },
  { delay: 900, label: "데이터 조회 중" },
  { delay: 2400, label: "결과 정리 중" },
  { delay: 4800, label: "답변 작성 중" },
];

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatNumber(value) {
  const number = toNumber(value);
  if (number === null) return "";
  return number.toLocaleString("ko-KR");
}

function formatDuration(seconds) {
  const value = toNumber(seconds);
  if (!value || value <= 0) return "";
  if (value < 60) return `${Math.ceil(value)}초`;
  return `${Math.ceil(value / 60)}분`;
}

function getUsageRatio(remaining, limit) {
  const remainingNumber = toNumber(remaining);
  const limitNumber = toNumber(limit);
  if (remainingNumber === null || !limitNumber || limitNumber <= 0) return null;
  return Math.min(1, Math.max(0, (limitNumber - remainingNumber) / limitNumber));
}

function getLimitGauge(rateLimit, quotaStatus = "idle") {
  if (quotaStatus === "credit-low") {
    return {
      ratio: 1,
      fillPercent: 100,
      tone: "danger",
      text: "결제 크레딧 부족",
    };
  }

  if (!rateLimit) {
    return {
      ratio: 0,
      fillPercent: 0,
      tone: "safe",
      text: quotaStatus === "checking" ? "API 처리 한도 확인 중" : "API 처리 한도 확인 전",
    };
  }

  const retryAfter = toNumber(rateLimit.retry_after_seconds);
  const ratios = [
    getUsageRatio(rateLimit.requests_remaining, rateLimit.requests_limit),
    getUsageRatio(rateLimit.tokens_remaining, rateLimit.tokens_limit),
    getUsageRatio(rateLimit.input_tokens_remaining, rateLimit.input_tokens_limit),
    getUsageRatio(rateLimit.output_tokens_remaining, rateLimit.output_tokens_limit),
  ].filter((value) => value !== null);
  const ratio = retryAfter ? 1 : ratios.length ? Math.max(...ratios) : 0;
  const fillPercent = Math.round(ratio * 100);
  const tone = retryAfter || ratio >= 0.98 ? "danger" : ratio >= 0.5 ? "warning" : "safe";

  return {
    ratio,
    fillPercent,
    tone,
    text: `${fillPercent}% 사용`,
  };
}

function isRateLimitExhausted(rateLimit) {
  if (!rateLimit) return false;
  if (toNumber(rateLimit.retry_after_seconds) > 0) return true;

  return [
    [rateLimit.requests_remaining, rateLimit.requests_limit],
    [rateLimit.tokens_remaining, rateLimit.tokens_limit],
    [rateLimit.input_tokens_remaining, rateLimit.input_tokens_limit],
    [rateLimit.output_tokens_remaining, rateLimit.output_tokens_limit],
  ].some(([remaining, limit]) => {
    const remainingNumber = toNumber(remaining);
    const limitNumber = toNumber(limit);
    return limitNumber && limitNumber > 0 && remainingNumber !== null && remainingNumber <= 0;
  });
}

function getTokenLimitText(rateLimit, quotaStatus = "idle") {
  if (quotaStatus === "credit-low") return "Claude 결제 크레딧 부족";
  if (!rateLimit) return quotaStatus === "checking" ? "API 처리 한도 확인 중" : "API 처리 한도 확인 전";

  const retryAfterText = formatDuration(rateLimit.retry_after_seconds);
  if (retryAfterText) return `API 처리 한도 초과 · 약 ${retryAfterText} 후 재시도`;

  const totalRemaining = toNumber(rateLimit.tokens_remaining);
  if (totalRemaining !== null) return `API 처리 여유 · ${formatNumber(totalRemaining)}토큰`;

  const inputRemaining = toNumber(rateLimit.input_tokens_remaining);
  const outputRemaining = toNumber(rateLimit.output_tokens_remaining);

  if (inputRemaining !== null && outputRemaining !== null) {
    return `처리 여유 · 입력 ${formatNumber(inputRemaining)} · 출력 ${formatNumber(outputRemaining)}토큰`;
  }
  if (inputRemaining !== null) return `입력 처리 여유 · ${formatNumber(inputRemaining)}토큰`;
  if (outputRemaining !== null) return `출력 처리 여유 · ${formatNumber(outputRemaining)}토큰`;

  const requestRemaining = toNumber(rateLimit.requests_remaining);
  if (requestRemaining !== null) return `요청 ${formatNumber(requestRemaining)}회 남음`;

  return "API 처리 한도 확인 전";
}

function getRateLimitBlockedMessage(rateLimit) {
  const retryAfterText = formatDuration(rateLimit?.retry_after_seconds);
  return retryAfterText
    ? `Claude API 한도가 꽉 차서 지금은 질문을 보낼 수 없습니다. 약 ${retryAfterText} 후 다시 시도해주세요.`
    : "Claude API 한도가 꽉 차서 지금은 질문을 보낼 수 없습니다. 잠시 후 다시 시도해주세요.";
}

function createMessage(role, text, status = "done") {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    text,
    status,
    createdAt: new Date(),
  };
}

function getFriendlyErrorMessage(error) {
  const rawMessage = String(error?.message || "").trim();

  if (!rawMessage) return "AI 서버와 통신하지 못했습니다.";
  if (rawMessage.includes("Failed to fetch")) {
    return "AI 서버에 연결할 수 없습니다. 서버 주소 또는 네트워크 상태를 확인해주세요.";
  }
  if (rawMessage.includes("응답 시간이 초과")) return rawMessage;
  if (
    error?.code === "ANTHROPIC_CREDIT_LOW" ||
    error?.status === 402 ||
    rawMessage.toLowerCase().includes("credit balance is too low")
  ) {
    return "Claude API 크레딧이 부족합니다. Anthropic 결제/크레딧 충전 후 다시 시도해주세요.";
  }
  if (error?.code === "ANTHROPIC_RATE_LIMIT" || error?.status === 429) {
    return getRateLimitBlockedMessage(error?.rateLimit);
  }
  if (error?.status === 404) {
    return "AI 연결 경로를 찾지 못했습니다. 잠시 후 다시 시도해주세요.";
  }
  if (error?.status === 401) {
    return "AI 사용을 위해 로그인이 필요합니다. 다시 로그인해주세요.";
  }
  if (error?.status === 403) {
    return "AI 사용 권한이 없습니다.";
  }
  if (error?.status === 400 || rawMessage.includes("API 오류 400")) {
    return "조회 조건을 처리하는 중 문제가 생겼습니다. LOT 번호, 날짜, 농장명처럼 조건을 조금 더 구체적으로 다시 입력해주세요.";
  }
  if (error?.status >= 500) {
    return "조회 중 문제가 생겼습니다. 질문 조건을 조금 좁혀 다시 시도해주세요. 같은 문제가 반복되면 화면과 질문 내용을 관리자에게 전달해주세요.";
  }

  return rawMessage;
}

function isConnectionFailure(error) {
  const rawMessage = String(error?.message || "");
  if (rawMessage.includes("Failed to fetch")) return true;
  if (["AI_PROXY_UNAVAILABLE", "AI_PROXY_TIMEOUT"].includes(error?.code)) return true;
  return [502, 504].includes(error?.status) && !error?.code;
}

function StatusPill({ status }) {
  const labels = {
    checking: "연결 확인 중",
    online: "연결됨",
    offline: "오프라인",
    idle: "대기",
  };

  return (
    <span className={`chatbot-status chatbot-status--${status}`}>
      <span className="chatbot-status__dot" />
      {labels[status] || labels.idle}
    </span>
  );
}

function MessageBubble({ message, onRetry, onFeedback, onLearningDecision }) {
  const isAssistant = message.role === "assistant";
  const isLoading = message.status === "loading";
  const isError = message.status === "error";
  const learningAction = message.learningAction;
  const hasLearningAction = learningAction?.type === "confirm_learning";
  const isSavingLearning = message.learningStatus === "saving";
  const canDecideLearning = hasLearningAction && !["active", "rejected"].includes(message.learningStatus);
  const canGiveFeedback = isAssistant && message.status === "done" && message.responseId && !hasLearningAction;
  const isSavingFeedback = message.feedbackStatus === "saving";

  return (
    <div className={`chatbot-message chatbot-message--${message.role}`}>
      <div className={`chatbot-bubble${isError ? " chatbot-bubble--error" : ""}`}>
        {isLoading ? (
          <div className="chatbot-loading-wrap" aria-live="polite" aria-label="답변 생성 중">
            <div className="chatbot-loading" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <span className="chatbot-loading__label">
              {message.loadingLabel || "답변 준비 중"}
            </span>
          </div>
        ) : isAssistant ? (
          <Suspense fallback={message.text}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: (props) => {
                  const { children, ...linkProps } = props;
                  delete linkProps.node;
                  return (
                    <a {...linkProps} target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  );
                },
                table: (props) => {
                  const { children, ...tableProps } = props;
                  delete tableProps.node;
                  return (
                    <div className="chatbot-markdown-table">
                      <table {...tableProps}>{children}</table>
                    </div>
                  );
                },
              }}
            >
              {message.text}
            </ReactMarkdown>
          </Suspense>
        ) : (
          message.text
        )}
      </div>

      {isError && onRetry ? (
        <button type="button" className="chatbot-inline-action" onClick={onRetry}>
          다시 시도
        </button>
      ) : null}

      {canDecideLearning ? (
        <div className="chatbot-learning" aria-label="학습 여부 확인">
          <div className="chatbot-learning__actions">
            <button
              type="button"
              className="chatbot-learning__button chatbot-learning__button--confirm"
              onClick={() => onLearningDecision?.(message, "confirm")}
              disabled={isSavingLearning}
            >
              {isSavingLearning ? "저장 중" : "예"}
            </button>
            <button
              type="button"
              className="chatbot-learning__button"
              onClick={() => onLearningDecision?.(message, "reject")}
              disabled={isSavingLearning}
            >
              아니요
            </button>
          </div>
          {message.learningError ? (
            <p className="chatbot-learning__error" role="alert">{message.learningError}</p>
          ) : null}
        </div>
      ) : null}

      {["active", "rejected"].includes(message.learningStatus) ? (
        <p className={`chatbot-learning__result chatbot-learning__result--${message.learningStatus}`}>
          {message.learningMessage}
        </p>
      ) : null}

      {canGiveFeedback ? (
        <div className="chatbot-feedback" aria-label="답변 평가">
          <button
            type="button"
            className={message.feedback === "like" ? "chatbot-feedback__button is-active" : "chatbot-feedback__button"}
            onClick={() => onFeedback?.(message, "like")}
            disabled={isSavingFeedback}
            aria-pressed={message.feedback === "like"}
          >
            좋아요
          </button>
          <button
            type="button"
            className={message.feedback === "dislike" ? "chatbot-feedback__button is-active" : "chatbot-feedback__button"}
            onClick={() => onFeedback?.(message, "dislike")}
            disabled={isSavingFeedback}
            aria-pressed={message.feedback === "dislike"}
          >
            별로예요
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function ChatBot({ pagePath = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    createMessage("assistant", WELCOME_MESSAGE),
  ]);
  const [serverHistory, setServerHistory] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("idle");
  const [rateLimit, setRateLimit] = useState(null);
  const [quotaStatus, setQuotaStatus] = useState("idle");
  const [lastFailedText, setLastFailedText] = useState("");

  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const isMountedRef = useRef(true);
  const healthRequestIdRef = useRef(0);
  const quotaRequestIdRef = useRef(0);
  const loadingStageTimersRef = useRef([]);

  const hasUserMessages = useMemo(
    () => messages.some((message) => message.role === "user"),
    [messages]
  );
  const limitGauge = useMemo(() => getLimitGauge(rateLimit, quotaStatus), [quotaStatus, rateLimit]);
  const tokenLimitText = useMemo(() => getTokenLimitText(rateLimit, quotaStatus), [quotaStatus, rateLimit]);
  const limitGaugeStyle = useMemo(
    () => ({
      "--chatbot-limit-fill": `${limitGauge.fillPercent}%`,
    }),
    [limitGauge.fillPercent]
  );

  const scrollToBottom = useCallback((behavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = "0px";
    input.style.height = `${Math.min(input.scrollHeight, 116)}px`;
  }, []);

  const clearLoadingStageTimers = useCallback(() => {
    loadingStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    loadingStageTimersRef.current = [];
  }, []);

  const setLoadingStage = useCallback((messageId, label) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId && message.status === "loading"
          ? { ...message, loadingLabel: label }
          : message
      )
    );
  }, []);

  const startLoadingStages = useCallback(
    (messageId) => {
      clearLoadingStageTimers();
      loadingStageTimersRef.current = RESPONSE_STAGE_STEPS.map(({ delay, label }) =>
        window.setTimeout(() => setLoadingStage(messageId, label), delay)
      );
    },
    [clearLoadingStageTimers, setLoadingStage]
  );

  const refreshHealth = useCallback(async ({ silent = false } = {}) => {
    const requestId = healthRequestIdRef.current + 1;
    healthRequestIdRef.current = requestId;

    if (!silent) setConnectionStatus("checking");
    const health = await checkAiHealth({ timeoutMs: 3500 });
    const isHealthy = typeof health === "boolean" ? health : Boolean(health?.ok);
    if (!isMountedRef.current || requestId !== healthRequestIdRef.current) return isHealthy;
    if (health?.rateLimit) setRateLimit(health.rateLimit);
    setConnectionStatus(isHealthy ? "online" : "offline");
    return isHealthy;
  }, []);

  const refreshQuota = useCallback(async ({ force = false } = {}) => {
    if (!force && rateLimit) return rateLimit;

    const requestId = quotaRequestIdRef.current + 1;
    quotaRequestIdRef.current = requestId;
    setQuotaStatus("checking");

    try {
      const quota = await checkAiQuota({ timeoutMs: 10000 });
      if (!isMountedRef.current || requestId !== quotaRequestIdRef.current) return null;
      if (quota?.rateLimit) {
        setRateLimit(quota.rateLimit);
        setConnectionStatus("online");
        setQuotaStatus("ready");
      } else {
        setQuotaStatus("idle");
      }
      return quota?.rateLimit || null;
    } catch (error) {
      if (!isMountedRef.current || requestId !== quotaRequestIdRef.current) return null;
      if (error?.code === "ANTHROPIC_CREDIT_LOW") {
        setRateLimit(null);
        setQuotaStatus("credit-low");
      } else {
        if (error?.rateLimit) setRateLimit(error.rateLimit);
        setQuotaStatus(error?.rateLimit ? "ready" : "idle");
      }
      if (["ANTHROPIC_RATE_LIMIT", "ANTHROPIC_CREDIT_LOW"].includes(error?.code)) {
        setConnectionStatus("online");
      }
      return null;
    }
  }, [rateLimit]);

  const resetConversation = useCallback(() => {
    clearLoadingStageTimers();
    setMessages([createMessage("assistant", WELCOME_MESSAGE)]);
    setServerHistory([]);
    setConversationId(null);
    setDraft("");
    setLastFailedText("");
    refreshQuota({ force: true });
    window.setTimeout(() => {
      resizeInput();
      inputRef.current?.focus();
    }, 0);
  }, [clearLoadingStageTimers, refreshQuota, resizeInput]);

  const submitMessage = useCallback(
    async (rawText) => {
      const text = String(rawText ?? draft).trim();
      if (!text || isSending) return;

      setIsSending(true);
      setConnectionStatus("checking");

      let pendingMessageId = null;

      try {
        let checkedRateLimit = rateLimit;
        if (isRateLimitExhausted(checkedRateLimit)) {
          checkedRateLimit = await refreshQuota({ force: true }) || checkedRateLimit;
        }

        if (isRateLimitExhausted(checkedRateLimit)) {
          setDraft("");
          setLastFailedText(text);
          setConnectionStatus("online");
          setMessages((prev) => [
            ...prev,
            createMessage("user", text),
            {
              ...createMessage("assistant", getRateLimitBlockedMessage(checkedRateLimit), "error"),
              question: text,
            },
          ]);
          window.setTimeout(resizeInput, 0);
          return;
        }

        pendingMessageId = createMessage("assistant", "", "loading").id;
        setDraft("");
        setLastFailedText("");
        healthRequestIdRef.current += 1;
        setMessages((prev) => [
          ...prev,
          createMessage("user", text),
          {
            id: pendingMessageId,
            role: "assistant",
            text: "",
            status: "loading",
            loadingLabel: RESPONSE_STAGE_STEPS[0].label,
            createdAt: new Date(),
            question: text,
          },
        ]);

        startLoadingStages(pendingMessageId);
        window.setTimeout(resizeInput, 0);

        const {
          reply,
          history,
          conversationId: nextConversationId,
          responseId,
          rateLimit: nextRateLimit,
          action,
        } = await sendChatMessage(text, serverHistory, { conversationId, pagePath });
        if (!isMountedRef.current) return;

        setServerHistory(history);
        if (nextRateLimit) {
          setRateLimit(nextRateLimit);
        }
        setQuotaStatus(nextRateLimit ? "ready" : "idle");
        if (nextConversationId) setConversationId(nextConversationId);
        setConnectionStatus("online");
        setMessages((prev) =>
          prev.map((message) =>
            message.id === pendingMessageId
              ? {
                  ...message,
                  text: reply || "응답 내용이 비어 있습니다.",
                  status: "done",
                  responseId,
                  conversationId: nextConversationId || conversationId,
                  learningAction: action,
                  learningStatus: action ? "pending" : null,
                }
              : message
          )
        );
      } catch (error) {
        if (!isMountedRef.current) return;

        if (error?.code === "ANTHROPIC_CREDIT_LOW") {
          setRateLimit(null);
          setQuotaStatus("credit-low");
        } else if (error?.rateLimit) {
          setRateLimit(error.rateLimit);
        }
        setConnectionStatus(isConnectionFailure(error) ? "offline" : "online");
        setLastFailedText(text);
        if (pendingMessageId) {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === pendingMessageId
                ? {
                    ...message,
                    text: getFriendlyErrorMessage(error),
                    status: "error",
                  }
                : message
            )
          );
        } else {
          setDraft("");
          setMessages((prev) => [
            ...prev,
            createMessage("user", text),
            {
              ...createMessage("assistant", getFriendlyErrorMessage(error), "error"),
              question: text,
            },
          ]);
          window.setTimeout(resizeInput, 0);
        }
      } finally {
        clearLoadingStageTimers();
        if (isMountedRef.current) {
          setIsSending(false);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }
      }
    },
    [
      clearLoadingStageTimers,
      conversationId,
      draft,
      isSending,
      pagePath,
      rateLimit,
      refreshQuota,
      resizeInput,
      serverHistory,
      startLoadingStages,
    ]
  );

  const handleFeedback = useCallback(async (message, rating) => {
    if (!message?.responseId || message.feedbackStatus === "saving") return;

    setMessages((prev) =>
      prev.map((item) =>
        item.id === message.id
          ? {
              ...item,
              feedbackStatus: "saving",
              feedbackError: "",
            }
          : item
      )
    );

    try {
      await sendChatFeedback({
        conversationId: message.conversationId || conversationId,
        responseId: message.responseId,
        rating,
        question: message.question,
        answer: message.text,
      });

      if (!isMountedRef.current) return;
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? {
                ...item,
                feedback: rating,
                feedbackStatus: "saved",
                feedbackError: "",
              }
            : item
        )
      );
    } catch (error) {
      if (!isMountedRef.current) return;
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? {
                ...item,
                feedbackStatus: "error",
                feedbackError: error?.message || "피드백 저장 실패",
              }
            : item
        )
      );
    }
  }, [conversationId]);

  const handleLearningDecision = useCallback(async (message, decision) => {
    const proposalId = message?.learningAction?.proposalId;
    if (!proposalId || message.learningStatus === "saving") return;

    setMessages((prev) =>
      prev.map((item) =>
        item.id === message.id
          ? { ...item, learningStatus: "saving", learningError: "" }
          : item
      )
    );

    try {
      const result = await decideAiKnowledge(proposalId, decision);
      if (!isMountedRef.current) return;

      const nextStatus = result.status === "active" ? "active" : "rejected";
      const resultMessage = result.message || (
        nextStatus === "active" ? "학습했습니다." : "학습하지 않았습니다."
      );
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? {
                ...item,
                learningStatus: nextStatus,
                learningMessage: resultMessage,
                learningError: "",
              }
            : item
        )
      );
      setServerHistory((prev) => [
        ...prev,
        { role: "user", content: decision === "confirm" ? "예" : "아니요" },
        { role: "assistant", content: resultMessage },
      ].slice(-8));
    } catch (error) {
      if (!isMountedRef.current) return;
      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? {
                ...item,
                learningStatus: "error",
                learningError: getFriendlyErrorMessage(error),
              }
            : item
        )
      );
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearLoadingStageTimers();
    };
  }, [clearLoadingStageTimers]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isOpen) return;

    window.setTimeout(() => inputRef.current?.focus(), 50);
    scrollToBottom("auto");
    if (!rateLimit) {
      refreshQuota();
    }
  }, [isOpen, rateLimit, refreshQuota, scrollToBottom]);

  useEffect(() => {
    if (!isOpen) return undefined;

    refreshHealth({ silent: false });
    return undefined;
  }, [isOpen, refreshHealth]);

  useEffect(() => {
    resizeInput();
  }, [draft, resizeInput]);

  function handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  }

  function handleQuickPrompt(prompt) {
    submitMessage(prompt);
  }

  return (
    <div className="chatbot-root">
      <button
        type="button"
        className={`chatbot-fab chatbot-fab--${connectionStatus}`}
        onClick={() => setIsOpen((value) => !value)}
        aria-label={isOpen ? "해미리 닫기" : "해미리 열기"}
        aria-expanded={isOpen}
      >
        <img
          className="chatbot-fab__image"
          src={`${process.env.PUBLIC_URL}/haemiri.png`}
          alt=""
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <section
          className="chatbot-window"
          role="dialog"
          aria-labelledby="chatbot-title"
          aria-live="polite"
        >
          <header className="chatbot-header">
            <div className="chatbot-header__main">
              <span
                className={`chatbot-header__mark chatbot-header__mark--${limitGauge.tone}`}
                style={limitGaugeStyle}
                title={`Claude API 처리 한도 · ${limitGauge.text}`}
                aria-hidden="true"
              >
                <span className="chatbot-header__mark-text">한도</span>
              </span>
              <div className="chatbot-header__text">
                <h2 id="chatbot-title">해미리</h2>
                <p className={`chatbot-header__quota chatbot-header__quota--${limitGauge.tone}`}>
                  {tokenLimitText}
                </p>
              </div>
            </div>
            <div className="chatbot-header__actions">
              <StatusPill status={connectionStatus} />
              <button
                type="button"
                className="chatbot-icon-button"
                onClick={resetConversation}
                disabled={isSending}
                title="새 대화"
                aria-label="새 대화"
              >
                <SystemIcon name="refresh" size="xsmall" />
              </button>
              <button
                type="button"
                className="chatbot-icon-button"
                onClick={() => setIsOpen(false)}
                title="닫기"
                aria-label="닫기"
              >
                <SystemIcon name="close" size="xsmall" />
              </button>
            </div>
          </header>

          <div className="chatbot-messages">
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                onRetry={
                  message.status === "error" && lastFailedText
                    ? () => submitMessage(lastFailedText)
                    : null
                }
                onFeedback={handleFeedback}
                onLearningDecision={handleLearningDecision}
              />
            ))}
            <div ref={bottomRef} />
          </div>

          {!hasUserMessages ? (
            <div className="chatbot-prompts" aria-label="빠른 질문">
              {QUICK_PROMPTS.map((prompt) => (
                <button
                  type="button"
                  key={prompt}
                  onClick={() => handleQuickPrompt(prompt)}
                  disabled={isSending}
                >
                  {prompt}
                </button>
              ))}
            </div>
          ) : null}

          <form
            className="chatbot-input-area"
            onSubmit={(event) => {
              event.preventDefault();
              submitMessage();
            }}
          >
            <textarea
              ref={inputRef}
              className="chatbot-input"
              placeholder="질문을 입력하세요"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <button
              type="submit"
              className="chatbot-send"
              disabled={!draft.trim() || isSending}
              aria-label="전송"
              title="전송"
            >
              <SystemIcon name="send" size="small" />
            </button>
          </form>
        </section>
      ) : null}
    </div>
  );
}
